import { ProseDensityProvider } from '@heidi/answer-kit';
import type { JSX, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { PLAN_COLLAPSE_DISABLED } from '../../../../data/projects/timeline/agent-todo/collapsePlanSteps';
import type { LatestTodoPlan } from '../../../../data/projects/timeline/agent-todo/computeLatestTodoPlan';
import { planCardTitle } from '../../../../data/projects/timeline/agent-todo/planCardTitle';
import type { PlanProgress } from '../../../../data/projects/timeline/agent-todo/planProgress';
import { computePlanProgress } from '../../../../data/projects/timeline/agent-todo/planProgress';
import type { AgentTodoItem } from '../../../../data/projects/timeline/agent-todo/types';
import { usePlanAnchorOffScreen } from '../../../../data/projects/timeline/agent-todo/usePlanAnchorOffScreen';
import { useStaggeredTodoItems } from '../../../../data/projects/timeline/agent-todo/useStaggeredTodoItems';
import { PlanProgressBar } from './PlanProgressBar';
import { PlanStepsPanel } from './PlanStepsPanel';

interface PlanProgressDockSurfaceProps {
  title: string;
  progress: PlanProgress;
  items: readonly AgentTodoItem[];
}

/**
 * The bar and the plan it opens. Mounted only while the summary is visible, so
 * its open state cannot survive a hide — the dock always returns collapsed.
 */
const PlanProgressDockSurface = ({
  title,
  progress,
  items,
}: PlanProgressDockSurfaceProps): JSX.Element => {
  const staggeredItems = useStaggeredTodoItems(items);
  const [isOpen, setIsOpen] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);

  // On document: macOS WebKit doesn't focus a clicked button, so a handler on
  // the surface never hears Escape. Outside-pointer is also touch's only exit.
  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setIsOpen(false);
    };
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const surface = surfaceRef.current;
      if (surface && !surface.contains(event.target as Node)) setIsOpen(false);
    };

    // Capture + preventDefault claims Esc, so page-wide Esc bindings (e.g. stop generating) stand down.
    document.addEventListener('keydown', closeOnEscape, { capture: true });
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => {
      document.removeEventListener('keydown', closeOnEscape, { capture: true });
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
    };
  }, [isOpen]);

  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-start justify-center px-6 pt-3">
      <div
        ref={surfaceRef}
        className="pointer-events-auto flex max-h-full min-h-0 w-full max-w-3xl flex-col"
        onMouseLeave={() => setIsOpen(false)}
      >
        <PlanProgressBar
          title={title}
          completed={progress.completed}
          total={progress.total}
          currentStepTitle={progress.currentStepTitle}
          expanded={isOpen}
          // The closed bar renders the DS `rounded-md`; keep that on the top
          // corners and only square the bottom on open to meet the panel.
          className={isOpen ? 'rounded-b-none' : undefined}
          onSelect={() => setIsOpen((previous) => !previous)}
        />
        {isOpen ? (
          // Bounded by the transcript it floats in, not the viewport.
          <div
            className="min-h-0 flex-1 overflow-y-auto rounded-b-md border border-t-0 border-border bg-surface p-2 shadow-sm motion-safe:duration-200 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1"
            data-testid="plan-progress-expanded"
          >
            {/* Chrome pinned over the thread, sized with its bar, not the prose. */}
            <ProseDensityProvider density="compact">
              <PlanStepsPanel
                items={staggeredItems}
                collapseAfter={PLAN_COLLAPSE_DISABLED}
              />
            </ProseDensityProvider>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export interface PlanProgressDockProps {
  plan: LatestTodoPlan | null;
  /** Transcript scroll container — the observation root. */
  scrollRef: RefObject<HTMLElement>;
  /** A turn is running; a finished run keeps its plan inline only. */
  isRunActive: boolean;
  projectTitle?: string;
}

/**
 * Decides whether the pinned plan summary belongs over the transcript. Absolutely
 * positioned so it adds no height to the scroll content, which the follow-scroll
 * maths measures. Parent timeline only — the anchor lives out here, so child-run
 * panels are passed a null plan.
 *
 * Clicking opens the plan in place rather than scrolling back to the inline
 * card: the transcript stays where the reader left it, and the plan is one
 * click away instead of a scroll journey there and back.
 */
export const PlanProgressDock = ({
  plan,
  scrollRef,
  isRunActive,
  projectTitle,
}: PlanProgressDockProps): JSX.Element | null => {
  const intl = useIntl();
  const offScreen = usePlanAnchorOffScreen(plan?.anchorId ?? null, scrollRef);
  // `isComplete` already covers an empty or all-cancelled plan.
  const progress = plan ? computePlanProgress(plan.state) : null;
  const visible =
    !!plan && isRunActive && offScreen && !!progress && !progress.isComplete;

  return visible ? (
    <PlanProgressDockSurface
      title={planCardTitle(plan.state, intl, projectTitle)}
      progress={progress}
      items={plan.state.items}
    />
  ) : null;
};
